import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { DocumentUpload } from '../DocumentUpload';

interface DetailedEducationStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedEducationStep: React.FC<DetailedEducationStepProps> = ({
  onNext,
  onBack: _onBack,
  onClose,
  existingData,
  driver: _driver,
  isFirstStep: _isFirstStep,
  isLastStep: _isLastStep,
}) => {
  const [formData, setFormData] = useState({
    educationSchoolId: '',
    schoolNameOther: '',
    programTitle: '',
    completedDate: '',
    hasDocument: '',
    status: 'completed',
    documentFile: null,
  });

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData }));
    }
  }, [existingData]);

  // Mock driving schools - in real app, this would come from API
  const drivingSchools = [
    { value: '1', label: 'ABC Driving School' },
    { value: '2', label: 'City Driving Academy' },
    { value: '3', label: 'Professional Drivers Institute' },
    { value: '4', label: 'Highway Safety School' },
    { value: '5', label: 'Other' },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Prepare data for saving
    const saveData = {
      educationSchoolId: parseInt(formData.educationSchoolId, 10) || 1,
      schoolNameOther: formData.educationSchoolId === '5' ? formData.schoolNameOther : null,
      programTitle: formData.programTitle,
      completedDate: formData.completedDate,
      status: formData.status,
    };

    onNext(saveData);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        Add Driving Education
      </Typography>

      <Grid container spacing={3}>
        {/* Driving School */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Driving School
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select School</InputLabel>
            <Select
              value={formData.educationSchoolId}
              label="Select School"
              onChange={e => handleInputChange('educationSchoolId', e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              {drivingSchools.map(school => (
                <MenuItem key={school.value} value={school.value}>
                  {school.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Other school name input */}
          {formData.educationSchoolId === '5' && (
            <TextField
              fullWidth
              label="School Name"
              value={formData.schoolNameOther}
              onChange={e => handleInputChange('schoolNameOther', e.target.value)}
              sx={{ mt: 2, backgroundColor: 'white' }}
            />
          )}
        </Grid>

        {/* Program Title */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Program Title
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter program title"
            value={formData.programTitle}
            onChange={e => handleInputChange('programTitle', e.target.value)}
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>

        {/* Completed Date */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Completed Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.completedDate}
            onChange={e => handleInputChange('completedDate', e.target.value)}
            sx={{ backgroundColor: 'white' }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* Document Question */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Do you have a education/training document (file)?
          </Typography>
          <RadioGroup
            value={formData.hasDocument}
            onChange={e => handleInputChange('hasDocument', e.target.value)}
            sx={{ flexDirection: 'row' }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </Grid>

        {/* Document Upload */}
        {formData.hasDocument === 'yes' && (
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload Education Document
            </Typography>
            <DocumentUpload
              documentType="education"
              acceptTypes="image/*,.pdf,.doc,.docx"
              maxSizeMB={10}
              driverId={driver?.id}
              onUploadSuccess={fileData => {
                handleInputChange('documentFile', fileData);
              }}
              onUploadError={error => {
                console.error('Upload error:', error);
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 6,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderColor: '#1976d2',
            color: '#1976d2',
          }}
        >
          Close
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={
            !formData.educationSchoolId || !formData.programTitle || !formData.completedDate
          }
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
            px: 4,
            py: 1.5,
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
